/* eslint-disable max-len*/
import { ReactElement, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ActionButton from 'src/components/ActionButton/ActionButton';
import Modal from 'src/components/Modal/Modal';
import StyledSingleDropdown from 'src/components/StyledSingleDropdown/StyledSingleDropdown';
import {
  selectSeniorSnapshotDataCollectionCheck,
  selectSeniorSnapshotFormViewModel,
  selectSeniorSnapshotListItems,
  selectSeniorSnapshotLoadingFormViewModel,
  selectSeniorSnapshotLoadingListItems,
  setSeniorSnapshotLea,
  setSeniorSnapshotReviewStatus,
  setSeniorSnapshotSchool,
  setSeniorSnapshotSchoolYear,
} from 'src/redux/slices/seniorSnapshotSlice';
import {
  approveSeniorSnapshotsThunk,
  fetchDataCollectionCheck,
  fetchSeniorSnapshotLeas,
  fetchSeniorSnapshotListItems,
  fetchSeniorSnapshotSchoolsByLeaId,
  fetchSeniorSnapshotSchoolYears,
} from 'src/redux/thunks/seniorSnapshotThunks';
import { DialogType } from 'src/types/DialogTypes';
import { FeatureAccessPermissions } from 'src/types/FeatureAccessPermissions';
import { SeniorSnapshotListItemModel } from 'src/types/SeniorSnapshotListItemModel';
import {
  hasSpecifiedFeatureAccess,
  isSPO,
  isStateAdmin,
} from 'src/utilities/userUtilities';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { TooltipAnchor } from '../../../../services/app.service';
import { abortPromiseOnUnmount } from '../../../../services/base.service';
import { appRoutePaths } from '../../../../services/route.service';
import Student from '../StudentPage';
import './SeniorSnapshotList.css';

const SeniorShapshotList = (): ReactElement => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const hasResetOnReturnRef = useRef<boolean>(false);
  const formViewModel = useAppSelector(selectSeniorSnapshotFormViewModel);
  const listItemsIsLoading = useAppSelector(
    selectSeniorSnapshotLoadingListItems
  );
  const formViewModelIsLoading = useAppSelector(
    selectSeniorSnapshotLoadingFormViewModel
  );
  const listItems: SeniorSnapshotListItemModel[] = useAppSelector(
    selectSeniorSnapshotListItems
  );
  const [isSchoolDisabled, setIsSchoolDisabled] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showApproveModal, setShowApproveModal] = useState(false);
  const canApprove =
    (isSPO() &&
      hasSpecifiedFeatureAccess(
        FeatureAccessPermissions.SENIOR_SHAPSHOT_ACCESS
      )) ||
    isStateAdmin();
  const isPreviousSchoolYear = useAppSelector(selectSeniorSnapshotDataCollectionCheck);

  // using same reviewStatusOptions as input page, but needs to be updated
  const getFormViewModelReviewStatusOptions =
    formViewModel?.reviewStatusOptions.map((obj) => {
      const keys = Object.keys(obj) as (keyof typeof obj)[];
      const lastKey = keys[keys.length - 1];
      // Create a shallow copy and delete the last key
      const rest = { ...obj };
      delete rest[lastKey];
      return rest;
    });

  useEffect(() => {
    const schoolYearsPromise: unknown = dispatch(fetchSeniorSnapshotSchoolYears());
    (schoolYearsPromise as Promise<unknown>).then((result) => {
      if (
        fetchSeniorSnapshotSchoolYears.fulfilled.match(result as ReturnType<typeof fetchSeniorSnapshotSchoolYears.fulfilled>) &&
        (result as ReturnType<typeof fetchSeniorSnapshotSchoolYears.fulfilled>).payload?.length > 0
      ) {
        const firstSchoolYear = (result as ReturnType<typeof fetchSeniorSnapshotSchoolYears.fulfilled>).payload[0].value;
        dispatch(fetchDataCollectionCheck(firstSchoolYear));
      }
    });
    dispatch(setSeniorSnapshotLea({ value: '', label: '' }));
    dispatch(setSeniorSnapshotSchool({ value: '', label: '' }));
    dispatch(setSeniorSnapshotReviewStatus({ value: '', label: '' }));
    return () => {
      abortPromiseOnUnmount(schoolYearsPromise);
    };
  }, [dispatch]);

  useEffect(() => {
    const promise: unknown = dispatch(fetchSeniorSnapshotLeas());
    return () => {
      abortPromiseOnUnmount(promise);
    };
  }, [dispatch]);

  // fetch listItems only after school year and lea options are fetched
  useEffect(() => {
    if (formViewModel?.schoolYearOptions.length > 0) {
      const schoolYearOptions = formViewModel.schoolYearOptions;
      const promise: unknown = dispatch(
        fetchSeniorSnapshotListItems({
          schoolYear: schoolYearOptions[0].value,
          lea: '',
          school: '',
          reviewStatus: '',
        })
      );
      return () => {
        abortPromiseOnUnmount(promise);
      };
    }
  }, [dispatch, formViewModel?.schoolYearOptions]);

  // Reset filters when returning from input page (cancel/back button)
  useEffect(() => {
    const returningFromInput = sessionStorage.getItem(
      'seniorSnapshotReturningFromInput'
    );
    const isListPage = location.pathname === appRoutePaths.SeniorSnapshotList;

    // Reset all filter states when returning from input page
    if (
      isListPage &&
      returningFromInput === 'true' &&
      formViewModel?.schoolYearOptions.length > 0 &&
      !hasResetOnReturnRef.current
    ) {
      // Reset all filters to default/empty values
      dispatch(setSeniorSnapshotSchoolYear(formViewModel?.defaultSchoolYear));
      dispatch(setSeniorSnapshotLea({ value: '', label: '' }));
      dispatch(setSeniorSnapshotSchool({ value: '', label: '' }));
      dispatch(setSeniorSnapshotReviewStatus({ value: '', label: '' }));
      dispatch(
        fetchSeniorSnapshotListItems({
          schoolYear: formViewModel?.defaultSchoolYear?.value || '',
          lea: '',
          school: '',
          reviewStatus: '',
        })
      );
      dispatch(fetchDataCollectionCheck(formViewModel?.defaultSchoolYear?.value || ''));
      setIsSchoolDisabled(true);

      // Clear the flag and mark as reset
      sessionStorage.removeItem('seniorSnapshotReturningFromInput');
      hasResetOnReturnRef.current = true;
    }
  }, [
    location.pathname,
    dispatch,
    formViewModel?.defaultSchoolYear,
    formViewModel?.schoolYearOptions,
  ]);

  const selectedSchoolYearOption = {
    value: formViewModel?.selectedSchoolYear?.value || '',
    label: formViewModel?.selectedSchoolYear?.label || '',
  };

  const selectedLeaOption = {
    value: formViewModel?.selectedLea?.value || '',
    label: formViewModel?.selectedLea?.label || '',
  };

  const selectedSchoolOption = {
    value: formViewModel?.selectedSchool?.value || '',
    label: formViewModel?.selectedSchool?.label || '',
  };

  const selectedReviewStatusOption = {
    value: formViewModel?.selectedReviewStatus?.value || '',
    label: formViewModel?.selectedReviewStatus?.label || '',
  };
  const handleEdit = (id: string): void => {
    // Set flag to indicate we're navigating to input page
    sessionStorage.setItem('seniorSnapshotReturningFromInput', 'true');
    // Reset the ref so we can detect return on next visit
    hasResetOnReturnRef.current = false;
    navigate(appRoutePaths.SeniorSnapshotInput(id));
  };

  const handleResetFilter = (): void => {
    // we will set school year to default value & reset other filters; After that dispatch fetchSeniorSnapshotListItems
    dispatch(setSeniorSnapshotSchoolYear(formViewModel?.defaultSchoolYear));
    dispatch(setSeniorSnapshotLea({ value: '', label: '' }));
    dispatch(setSeniorSnapshotSchool({ value: '', label: '' }));
    dispatch(setSeniorSnapshotReviewStatus({ value: '', label: '' }));
    dispatch(
      fetchSeniorSnapshotListItems({
        schoolYear: formViewModel?.defaultSchoolYear?.value,
        lea: '',
        school: '',
        reviewStatus: '',
      })
    );
    dispatch(fetchDataCollectionCheck(formViewModel?.defaultSchoolYear?.value || ''));
    setIsSchoolDisabled(true);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleOnChangeOfSchoolYear = async (option: any): Promise<void> => {
    option && dispatch(setSeniorSnapshotSchoolYear(option));
  };

  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  const handleOnLeaChange = async (option: any): Promise<void> => {
    // Logic to handle school change
    if (!option) {
      setIsSchoolDisabled(true);
    } else {
      setIsSchoolDisabled(false);
      dispatch(setSeniorSnapshotLea(option));
      dispatch(fetchSeniorSnapshotSchoolsByLeaId(option?.value));
    }
  };

  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  const handleOnChangeOfSchool = async (option: any): Promise<void> => {
    option && dispatch(setSeniorSnapshotSchool(option));
  };

  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  const handleOnChangeOfReviewStatus = async (option: any): Promise<void> => {
    dispatch(setSeniorSnapshotReviewStatus(option));
  };

  const handleApplyFilter = (): void => {
    dispatch(
      fetchSeniorSnapshotListItems({
        schoolYear: formViewModel?.selectedSchoolYear?.value,
        lea: formViewModel?.selectedLea?.value,
        school: formViewModel?.selectedSchool?.value,
        reviewStatus: formViewModel?.selectedReviewStatus?.value,
      })
    );
    dispatch(fetchDataCollectionCheck(formViewModel?.selectedSchoolYear?.value || ''));
  };

  const pendingApprovalItems =
    listItems?.filter((item) => item.reviewStatus === 'Pending Approval') || [];

  const allPendingSelected =
    pendingApprovalItems.length > 0 &&
    pendingApprovalItems.every((item) => selectedIds.has(item.id));

  const handleSelectAll = (checked: boolean): void => {
    if (checked) {
      const newSelectedIds = new Set(selectedIds);
      pendingApprovalItems.forEach((item) => newSelectedIds.add(item.id));
      setSelectedIds(newSelectedIds);
    } else {
      const newSelectedIds = new Set(selectedIds);
      pendingApprovalItems.forEach((item) => newSelectedIds.delete(item.id));
      setSelectedIds(newSelectedIds);
    }
  };

  const handleSelectItem = (id: string, checked: boolean): void => {
    const newSelectedIds = new Set(selectedIds);
    if (checked) {
      newSelectedIds.add(id);
    } else {
      newSelectedIds.delete(id);
    }
    setSelectedIds(newSelectedIds);
  };

  const handleApproveClick = (): void => {
    setShowApproveModal(true);
  };

  const handleConfirmApprove = async (): Promise<void> => {
    setShowApproveModal(false);
    const idsToApprove = Array.from(selectedIds);
    const result = await dispatch(approveSeniorSnapshotsThunk(idsToApprove));
    if (approveSeniorSnapshotsThunk.fulfilled.match(result)) {
      setSelectedIds(new Set());
      dispatch(
        fetchSeniorSnapshotListItems({
          schoolYear: formViewModel?.selectedSchoolYear?.value || '',
          lea: formViewModel?.selectedLea?.value || '',
          school: formViewModel?.selectedSchool?.value || '',
          reviewStatus: formViewModel?.selectedReviewStatus?.value || '',
        })
      );
    }
  };

  const handleCancelApprove = (): void => {
    setShowApproveModal(false);
    setSelectedIds(new Set());
  };

  return (
    <Student
      pageClass="data-input--student--senior-snapshot--list"
      isLoading={listItemsIsLoading || formViewModelIsLoading}
      loadingDataId={'senior-snapshot-list-loader'}
    >
      <div className="content-outer-container">
        <div className="content-inner-container">
          <div className="subheading-and-button-container">
            <h4 data-testid={'component-heading-text'}>Senior Snapshot</h4>
          </div>
          <div className="filters-selection-container">
            <div className="dropdown-selection">
              <StyledSingleDropdown
                displayName="School Year"
                showLabel={true}
                labelText="School Year"
                fieldName="schoolYear"
                isClearable={false}
                isSearchable={false}
                options={formViewModel?.schoolYearOptions || []}
                value={selectedSchoolYearOption}
                onChange={handleOnChangeOfSchoolYear}
              />
            </div>
            <div className="dropdown-selection">
              <StyledSingleDropdown
                displayName="LEA"
                showLabel={true}
                labelText="LEA"
                fieldName="lea"
                isClearable={false}
                isSearchable={true}
                options={formViewModel?.leaOptions || []}
                value={selectedLeaOption}
                onChange={handleOnLeaChange}
              />
            </div>
            <div className="dropdown-selection">
              <StyledSingleDropdown
                displayName="School"
                showLabel={true}
                fieldName="school"
                labelText="School"
                isClearable={false}
                isSearchable={true}
                options={formViewModel?.schoolOptions || []}
                value={selectedSchoolOption}
                disabled={isSchoolDisabled}
                onChange={handleOnChangeOfSchool}
              />
            </div>
            <div className="dropdown-selection">
              <StyledSingleDropdown
                displayName="Status"
                labelText="Status"
                showLabel={true}
                fieldName="reviewStatus"
                isClearable={false}
                isSearchable={true}
                options={getFormViewModelReviewStatusOptions || []}
                value={selectedReviewStatusOption}
                onChange={handleOnChangeOfReviewStatus}
              />
            </div>
            <div className="button-group">
              <ActionButton
                classes="button apply-button"
                onClick={handleApplyFilter}
                dataTestId="apply-button"
              >
                <span>Apply Filter</span>
              </ActionButton>
              <ActionButton
                classes="button reset-button"
                onClick={handleResetFilter}
                dataTestId="reset-button"
              >
                <span>Reset Filter</span>
              </ActionButton>
            </div>
          </div>
          {canApprove &&
            isPreviousSchoolYear &&
            pendingApprovalItems.length > 0 && (
              <div className="approve-row">
                <input
                  type="checkbox"
                  data-testid="select-all-checkbox"
                  aria-label="Select all for approval"
                  checked={allPendingSelected}
                  disabled={pendingApprovalItems.length === 0}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  data-tooltip-id={TooltipAnchor}
                  data-tooltip-content={'Select All Pending Approval Status'}
                />
                <ActionButton
                  classes="button reset-button"
                  onClick={handleApproveClick}
                  dataTestId="approve-button"
                  disabled={selectedIds.size === 0}
                >
                  <span>Approve</span>
                </ActionButton>
              </div>
            )}
          <div className="table-container">
            <table cellSpacing={0} className="page-view-table">
              <thead>
                <tr>
                  <th>Student Name</th>
                  <th>MSIS ID</th>
                  <th>LEA</th>
                  <th>School</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {listItems?.length > 0
                  ? listItems.map((seniorSnapshot) => (
                    <tr
                      key={`${seniorSnapshot.id}`}
                      data-testid={seniorSnapshot.id}
                    >
                      <td>
                        {seniorSnapshot.studentLastOrSurname}
                        {', '}
                        {seniorSnapshot.studentFirstName}
                      </td>
                      <td>{seniorSnapshot.studentIdentifierState}</td>
                      <td>{seniorSnapshot.districtName}</td>
                      <td>{seniorSnapshot.schoolName}</td>
                      <td>{seniorSnapshot.reviewStatus}</td>
                      <td>
                        <div className="action--column">
                          {canApprove &&
                            isPreviousSchoolYear &&
                            seniorSnapshot.reviewStatus ===
                            'Pending Approval' && (
                              <input
                                type="checkbox"
                                data-testid={`select-checkbox-${seniorSnapshot.id}`}
                                aria-label="Select for approval"
                                checked={selectedIds.has(seniorSnapshot.id)}
                                onChange={(e) =>
                                  handleSelectItem(
                                    seniorSnapshot.id,
                                    e.target.checked
                                  )
                                }
                              />
                            )}
                          <button
                            data-testid={`edit-button-${seniorSnapshot.id}`}
                            aria-label="Edit"
                            onClick={() => handleEdit(seniorSnapshot.id)}
                            data-tooltip-id={TooltipAnchor}
                            data-tooltip-content={'Edit/View Senior Snapshot'}
                          >
                            <img src={'/images/icon-edit.svg'} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                  : !listItemsIsLoading && (
                    <tr>
                      <td
                        colSpan={6}
                        style={{ textAlign: 'center', padding: '20px' }}
                      >
                        No students with non-tested subjects.
                      </td>
                    </tr>
                  )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Modal
        type={DialogType.CONFIRM}
        open={showApproveModal}
        onClose={handleCancelApprove}
        title={'Are you sure?'}
      >
        <div className="confirm-container">
          <p data-testid="approve-confirm-message">
            Do you want to approve the selected record(s)?
          </p>
          <div className="button-row">
            <ActionButton
              classes="button"
              onClick={handleConfirmApprove}
              dataTestId="approve-confirm-yes"
              tooltipText={'Navigate to the last page'}
            >
              <span>Yes</span>
            </ActionButton>
            <ActionButton
              classes="button--secondary"
              onClick={handleCancelApprove}
              dataTestId="approve-confirm-no"
              tooltipText={'Stay on existing page'}
            >
              <span>No</span>
            </ActionButton>
          </div>
        </div>
      </Modal>
    </Student>
  );
};

export default SeniorShapshotList;
